import { MagnifyingGlassIcon, UserCircleIcon, UserIcon } from "@heroicons/react/20/solid";
import { PhoneIcon, EnvelopeIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { Button } from "../../Catalyst/button";
import { Divider } from "../../Catalyst/divider";
import { Input, InputGroup } from "../../Catalyst/input";
import BAckIcon from "../../../assets/BackIcon";
import { useEffect, useState } from "react";
import { fetchAllCoachNotes, CoachNote } from "../../../services/NotesService";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserProfileAsync, resetPasswordAsync, updateUserProfileAsync, updateUserStatusAsync, sendUserInviteAsync } from "../../../store/thunks/UsersThunk";
import { AppDispatch, RootState } from "../../../store/store";
import ActiveIcon from "../../../assets/ActiveIcon";
import { useToast } from "../../../contexts/ToastContext";
import SaveSuccessIcon from "../../../assets/SaveSuccessIcon";
import DeactiveSuccessIcon from "../../../assets/DeactiveSuccessIcon";
import { isEmpty } from "lodash";
import InputMask from 'react-input-mask';
import SendInviteIcon from "../../../assets/SendInviteIcon";
import UserProfileIcon from "../../../assets/UserProfileIcon";
import plusIcon from '../../../assets/plus.svg';
import SendIconGreen from '../../../assets/SendIconGreen.svg';
import AddNoteModal from './AddNoteModal';
import { SerializedEditorState } from "lexical";
import ViewNoteSection from "./ViewNoteSection";
import ViewNoteModal from "./ViewNoteModal";
import SignedUpIcon from "../../../assets/SignedUp";
import { USER_STATUS } from "../../common/userStatus";
const UserProfile = () => {
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    isFromGlobalSearch?: boolean;
    searchQuery?: string;
    // Set by callers that are not the global user list (e.g. the org-scoped
    // user list) so back returns to the page the admin actually came from.
    backTo?: string;
    backLabel?: string;
  } | null;
  const searchQuery = locationState?.searchQuery ?? "";
  const isFromGlobalSearch = locationState?.isFromGlobalSearch ?? false;
  const backTo = locationState?.backTo;

  const backLabel = isFromGlobalSearch
    ? "Back to search results"
    : backTo
      ? (locationState?.backLabel ?? "Back")
      : "Back to all users";

  const handleBackNavigation = () => {
    if (isFromGlobalSearch) {
      navigate(`/search${searchQuery ? `?${searchQuery}` : ""}`);
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(`/user-list-dashboard${orgId ? `/${orgId}` : ""}`);
    }
  };
  const { showSuccessToast, showErrorToast } = useToast();
  const updateProfileStatus = useSelector((state: RootState) => state.UserStore.updateProfileStatus);
  const userProfile = useSelector((state: RootState) => state.UserStore.userProfileData);
  const resetPasswordStatus = useSelector((state: RootState) => state.UserStore.resetPasswordStatus);
  const updateStatusStatus = useSelector((state: RootState) => state.UserStore.updateStatusStatus);
  const inviteStatus = useSelector((state: RootState) => state.UserStore.inviteStatus);
  const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);
  const isFinancialCoach = userAccount?.user_role === 'FINANCIAL_COACH';
  const isSuperAdmin = userAccount?.user_role === 'SUPER_ADMIN';
  const [value, setValue] = useState("");
  const [mask, setMask] = useState("+1 (999) 999-9999");
  // Log only when userProfile changes

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    uniqueId: "",
    assignedCoaches: "",
    externalBookingId: ""
  });

  // Track original data to detect changes
  const [originalData, setOriginalData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);

  // Persistent note data state
  const [noteData, setNoteData] = useState({
    externalLinks: [] as string[],
    currentLink: '',
    sessionOverview: null as SerializedEditorState | null,
    selectedCategories: [] as string[]
  });

  // Coach notes state
  const [coachNotes, setCoachNotes] = useState<CoachNote[]>([]);
  const [isViewNoteModalOpen, setIsViewNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<any>(null);
  // Pagination state for coach notes
  const [coachNotesPage, setCoachNotesPage] = useState(1);
  const coachNotesPerPage = 10; // You can adjust per page as needed
  const [coachNotesTotalPages, setCoachNotesTotalPages] = useState(1);

  // Fetch coach notes for user using NotesService
  useEffect(() => {
    const fetchNotes = async () => {
      if (!userId) return;
      try {
        const notes = await fetchAllCoachNotes(userId, coachNotesPage, coachNotesPerPage);
        // Defensive: ensure notes.items is always an array
          setCoachNotes(notes.items);
          setCoachNotesTotalPages(notes.totalPages || 1);
      } catch (err) {
        setCoachNotes([]);
        setCoachNotesTotalPages(1);
        showErrorToast('Failure', 'Failed to fetch coach notes');
      }
    };
    fetchNotes();
  }, [userId, coachNotesPage, coachNotesPerPage]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (userId) {
        const resultAction = await dispatch(fetchUserProfileAsync({ UserId: userId }));

        if (resultAction.meta.requestStatus === 'fulfilled') {
          if (resultAction.payload) {
            const newFormData = {
              firstName: resultAction.payload.first || "",
              lastName: resultAction.payload.last || "",
              email: resultAction.payload.email || "",
              phone: resultAction.payload.mobilePhone || "",
              organization: resultAction.payload.organization?.name || "",
              uniqueId: resultAction.payload.externalUniqueId || "",
              externalBookingId: resultAction.payload.externalBookingId || "",
              assignedCoaches: "", // If you get this from API, update here
            };

            const newOriginalData = {
              firstName: resultAction.payload.first || "",
              lastName: resultAction.payload.last || "",
              email: resultAction.payload.email || "",
              phone: resultAction.payload.mobilePhone || "",
            };

            setFormData(newFormData);
            setOriginalData(newOriginalData);
          }
        } else {
          console.error('Fetch failed:', resultAction);
        }
      }
    };

    fetchProfile();
  }, [userId, dispatch]);

  // Check if any editable field has changed
  const hasChanges = () => {
    return (
      formData.firstName !== originalData.firstName ||
      formData.lastName !== originalData.lastName ||
      formData.email !== originalData.email ||
      formData.phone !== originalData.phone
    );
  };


  const [errors, setErrors] = useState<any>({});

  const validate = (name: keyof any, value: string) => {
    let error = "";

    switch (name) {
      case "firstName":
      case "lastName":
        if (!value.trim()) error = "Required";
        break;
      case "email":
        if (!value) error = "Required";
        else if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(value))
          error = "Invalid email format";
        break;
      case "phone":

        if (value) {
          const phonePattern = /^(?:\+1 \(\d{3}\) \d{3}-\d{4}|\+91 \d{5} \d{5})$/;

          if (!phonePattern.test(value)) {
            error = "Invalid phone number.";
          }
        }
        break;
      case "org":
      case "uid":
      case "coach":
        if (!value.trim()) error = "Required";
        break;
    }

    setErrors((prev: any) => ({ ...prev, [name]: error }));
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name == 'phone') {
      const inputValue = e.target.value.replace(/\D/g, ""); // only digits
      setValue(e.target.value);

      if (inputValue.startsWith("1")) {
        setMask("+1 (999) 999-9999");
      } else {
        setMask("+91 999-999-9999");
      }
      setFormData((prev) => ({ ...prev, phone: value }));
      const unmaskedNumber = value.replace(/\D/g, "");
      console.log("Unmasked phone:", unmaskedNumber);
      validate("phone", value);
      return;
    }
    const cleanValue = name === "email" ? value.toLowerCase() : value;

    setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    validate(name as keyof FormData, cleanValue);
  };


  const handleSaveUserData = async () => {
    if (!userId || !userProfile) return;
    try {
      const userData = {
        first: formData.firstName,
        last: formData.lastName,
        email: formData.email,
        user_role: userProfile.role || "FINLEY_USER",
        mobile_phone: formData.phone,
        external_unique_id: userProfile.externalUniqueId || "",
        organization_id: userProfile.organization?.id || userProfile.organization_id
      };

      await dispatch(updateUserProfileAsync({ userId, userData })).unwrap();
      await dispatch(fetchUserProfileAsync({ UserId: userId }));

      // Reset original data to current form data after successful save
      setOriginalData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      });

      showSuccessToast(
        'Changes Saved',
        "User profile updated successfully.",
        <SaveSuccessIcon />
      );
    } catch (error) {
      let errMsg = 'Profile updates could not be saved.';
      if (
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        'msg' in error.data &&
        typeof (error.data as any).msg === 'string'
      ) {
        errMsg = (error.data as any).msg;
      } else if (error instanceof Error) {
        errMsg = error.message;
      }
      showErrorToast('Failure', errMsg);
    }
  };
  const handleDeactivateUser = async () => {
    if (!userId || !userProfile) return;
    try {
      await dispatch(updateUserStatusAsync({
        userId,
        isActive: !userProfile.isActive
      })).unwrap();
      await dispatch(fetchUserProfileAsync({ UserId: userId }));
      if (!userProfile.isActive) {
        showSuccessToast(
          'User Activated',
          `${userProfile.first} ${userProfile.last} has been activated successfully.`,
          <UserProfileIcon />
        );
      }
      else {
        showSuccessToast(
          'User Deactivated',
          `${userProfile.first} ${userProfile.last} has been deactivated successfully.`,
          <DeactiveSuccessIcon />
        );
      }

    } catch (error) {
      showErrorToast(
        'Failure',
        'Unable to deactivate user. Please try again.'
      );
    }
  };
  const handleResetPassword = async () => {
    const email = formData.email;
    try {
      await dispatch(resetPasswordAsync({ email })).unwrap();
      showSuccessToast(
        'Password Reset Email Sent',
        `${formData.firstName} ${formData.lastName} will receive password reset instructions.`
      );
    } catch (error) {
      showErrorToast(
        'Failure',
        'Password reset email could not be sent.'
      );
    }
  };

  const handleSendInvite = async () => {
    if (!userId || !userProfile) return;

    try {
      await dispatch(sendUserInviteAsync({ userId })).unwrap();
      showSuccessToast(
        'Invite Sent',
        `${userProfile.first} ${userProfile.last} has been invited successfully.`
      );
      await dispatch(fetchUserProfileAsync({ UserId: userId }));
    } catch (error) {
      showErrorToast(
        'Failure',
        'Unable to send invite. Please try again.'
      );
    }
  };

  const handleSaveNote = (savedNoteData: { externalLinks: string[], sessionOverview: string, selectedCategories: string[] }) => {

    // Reset the note data after successful save
    setNoteData({
      externalLinks: [],
      currentLink: '',
      sessionOverview: null,
      selectedCategories: []
    });

    // Refresh coach notes list
    if (userId) {
      fetchAllCoachNotes(userId, 1, coachNotesPerPage).then((notes) => {
        if (notes && Array.isArray(notes.items)) {
          setCoachNotes(notes.items);
        } else {
          setCoachNotes([]);
        }
      }).catch(() => setCoachNotes([]));
    }

    showSuccessToast(
      'Coaching Note Submitted Successfully',
      "Your Coaching Note has been submitted and added to the user's history.",
      <img src={SendIconGreen} alt="Note Added" />
    );
  };

  const handleCancelNote = () => {
    // Optionally, you could ask for confirmation if there's unsaved data
    // For now, just close without clearing data
    setIsAddNoteModalOpen(false);
  };

  const handleClearNote = () => {
    // Clear all note data (could be triggered by a "Clear" button)
    setNoteData({
      externalLinks: [],
      currentLink: '',
      sessionOverview: null,
      selectedCategories: []
    });
  };

  return (
    <div className="pt-8">
      <div className="flex flex-col items-start gap-5 self-stretch">
        {/* First inner div */}
        <div className="flex flex-col items-start gap-2.5 px-8 self-stretch">
          {/* Contents for first div */}

          {/* Nested div inside first inner div */}
          <div className="flex flex-col items-start gap-5 self-stretch">
            <Button
              plain
              onClick={handleBackNavigation}
              className="bg-textColor2 desktext13"
              aria-label="Previous page"
            >
              <BAckIcon />
              {backLabel}
            </Button>
          </div>

        </div>

        {/* Second inner div */}
        <div className="flex flex-col items-start gap-5 self-stretch">  {/* card header */}
          <div className="flex items-start pt-5 px-6 gap-4 self-stretch">  {/* content */}
            <div className="flex items-center gap-2 flex-[1_0_0]">  {/*Text and supporting text*/}
              <div className="desktext10 flex flex-col items-start gap-1 whitespace-nowrap">
                {userProfile?.first} {userProfile?.last}
              </div>
              <div className="flex items-center gap-3">
                {(() => {
                  const status = userProfile?.userStatus || USER_STATUS.ACTIVE;

                  const badgeMap: Record<string, { icon: JSX.Element; label: string; textClass: string }> = {
                    [USER_STATUS.DEACTIVATED]: { icon: <DeactiveSuccessIcon />, label: "Deactivated", textClass: "text-red-400" },
                    [USER_STATUS.INVITED]: { icon: <SendInviteIcon stroke="#878B93" />, label: "Invited", textClass: "text-[#878B93]" },
                    [USER_STATUS.ACTIVE]: { icon: <ActiveIcon />, label: "Active", textClass: "text-green-600" },
                    [USER_STATUS.SIGNED_UP]: { icon: <SignedUpIcon />, label: "Signed Up", textClass: "text-yellow-500" },
                  };

                  const badge = badgeMap[status] || badgeMap[USER_STATUS.ACTIVE];

                  return (
                    <div
                      className="desktext13 rounded-full border-1 border-neutral-50 shadow-sm px-[14px] py-[10px] flex items-center space-x-1 bg-neutral-50"
                      aria-label="User status"
                    >
                      {badge.icon}
                      <span className={`${badge.textClass} desktext13`}>{badge.label}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
              <div>
                {userProfile?.userStatus === USER_STATUS.INVITED ? (
                  <Button
                    onClick={handleDeactivateUser}
                    outline
                    className={
                    userProfile?.isActive
                      ? 'bg-[#FEF2F2] !text-[#B91C1C] border !border-[#FECACA] mr-4 desktext13'
                      : 'bg-neutral-50 !text-neutral-700 border !border-neutral-200 mr-4 rounded-md desktext13'
                  }
                  >
                    {userProfile?.isActive ? 'Cancel Invite' : 'Reactivate'}
                  </Button>
                ) : (
                  <Button
                  onClick={handleDeactivateUser}
                  outline
                  className={
                    userProfile?.isActive
                      ? 'bg-[#FEF2F2] !text-[#B91C1C] border !border-[#FECACA] mr-4 desktext13'
                      : 'bg-neutral-50 !text-neutral-700 border !border-neutral-200 mr-4 rounded-md desktext13'
                  }
                >
                  {
                    userProfile?.isActive ? 'Deactivate' : 'Reactivate'
                  }
                </Button>
              )}


              {userProfile?.userStatus === USER_STATUS.INVITED ? (
                <Button
                  outline
                  className="bg-textColor2  "
                  disabled={inviteStatus === 'loading' || userProfile?.isActive === false}
                  onClick={handleSendInvite}
                >
                  {inviteStatus === 'loading' ? 'Sending...' : 'Send Invite'}
                </Button>
              ) : (
                <Button
                  outline
                  className="bg-textColor2  "
                  onClick={handleResetPassword}
                  disabled={resetPasswordStatus === 'loading' || !!errors.email || userProfile?.isActive === false}
                >
                  Send Password Reset
                </Button>
              )}
            </div>

          </div>
          <Divider />
        </div>
      </div>
      <div className="flex items-start flex-[1_0_0] self-stretch">
        <div className="flex flex-col items-start py-5 p-6 flex-[1_0_0] basis-3/5 sticky top-0">
          <div className="flex flex-col items-start gap-5 self-stretch" > {/* sectio header */}
            <div className="flex items-start  gap-4 self-stretch">  {/* content */}
              <div className="flex flex-col justify-center items-start gap-1 flex-[1_0_0] self-stretch">{/* text and supporting text */}
                <div className="desktext11 ">User Info</div>
              </div>


            </div>
            <Divider />

          </div>
          <div
            className={`flex flex-col items-start p-[20px] gap-[20px] self-stretch ${userProfile?.is_active === false
              ? 'pointer-events-none opacity-50'
              : ''
              }`}
            style={userProfile?.is_active === false ? { filter: 'grayscale(100%)' } : {}}
          >

            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Name
              </div>
              <div className="w-[200px]">
                <Input
                  disabled={userProfile?.isActive === false || isFinancialCoach || userProfile?.userStatus === USER_STATUS.INVITED}
                  onChange={handleChange}
                  value={formData.firstName}
                  name="firstName"
                  placeholder="First Name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs">{errors.firstName}</p>
                )}</div>

              <div className="w-[200px]">
                <Input
                  disabled={userProfile?.isActive === false || isFinancialCoach || userProfile?.userStatus === USER_STATUS.INVITED}
                  onChange={handleChange}
                  value={formData.lastName}
                  name="lastName"
                  placeholder="Last Name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs">{errors.lastName}</p>
                )}
              </div>
            </div>
            <hr role="presentation" className="w-full border-t border-zinc-950/10 dark:border-white/10" />

            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Email Address
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                  <Input disabled={userProfile?.isActive === false || isFinancialCoach || userProfile?.userStatus === USER_STATUS.INVITED}
                    value={formData.email}
                    name="email"
                    onChange={handleChange}
                    placeholder="Enter Your Email"
                  />
                </InputGroup>
                {errors.email && (
                  <p className="text-red-500 text-xs">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Phone
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <PhoneIcon className="w-5 h-5 text-gray-400" />

                  <InputMask
                    mask={mask}
                    disabled={userProfile?.isActive === false || isFinancialCoach || userProfile?.userStatus === USER_STATUS.INVITED}
                    value={formData.phone}
                    onChange={handleChange}
                    name="phone"
                    maskChar=" "
                    className={`text-sm w-full border rounded-lg p-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-9 ${userProfile?.isActive === false || (isFinancialCoach && !isSuperAdmin) ? '!text-zinc-500' : 'text-zinc-950'}`}
                  />
                </InputGroup>
                {errors.phone && (
                  <p className="text-red-500 text-xs">{errors.phone}</p>
                )}
              </div>
            </div>
            <hr role="presentation" className="w-full border-t border-zinc-950/10 dark:border-white/10" />

            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Organization
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <Input
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Organization"
                    disabled
                  />
                </InputGroup>
                {errors.organization && (
                  <p className="text-red-500 text-xs">{errors.organization}</p>
                )}
              </div>
            </div>
            <hr role="presentation" className="w-full border-t border-zinc-950/10 dark:border-white/10" />
            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Unique ID
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <Input
                    name="uniqueId"
                    value={formData.uniqueId}
                    onChange={handleChange}
                    placeholder="Unique ID"
                    disabled

                  />
                </InputGroup>
                {errors.uniqueId && (
                  <p className="text-red-500 text-xs">{errors.uniqueId}</p>
                )}
              </div>
            </div>
            <hr role="presentation" className="w-full border-t border-zinc-950/10 dark:border-white/10" />
            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                External Booking ID
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <Input
                    name="uniqueId"
                    value={formData.externalBookingId}
                    onChange={handleChange}
                    placeholder="External Booking ID"
                    disabled

                  />
                </InputGroup>
                {errors.externalBookingId && (
                  <p className="text-red-500 text-xs">{errors.externalBookingId}</p>
                )}
              </div>
            </div>
            <hr role="presentation" className="w-full border-t border-zinc-950/10 dark:border-white/10" />
            <div className="flex items-start content-start flex-wrap gap-y-[16px] gap-x-[32px] self-stretch">
              <div
                className="text-sm font-medium text-gray-700 min-w-[200px] max-w-[280px]"
              >
                Assigned Coaches
              </div>
              <div className="w-[440px]">
                <InputGroup>
                  <Input
                    name="assignedCoaches"
                    value={formData.assignedCoaches}
                    onChange={handleChange}
                    placeholder="Search by Coach Name"
                    disabled={isFinancialCoach}
                  />
                </InputGroup>
                {errors.assignedCoaches && (
                  <p className="text-red-500 text-xs">{errors.assignedCoaches}</p>
                )}
              </div>

            </div>

            <div className="flex-col w-full">
              <div className="w-full pb-5">   <Divider></Divider></div>
              {(!isFinancialCoach || isSuperAdmin) && (
                <Button outline className="py-10 rounded-lg border border-buttonBorder bg-textColor2"

                  onClick={() => { handleSaveUserData() }}
                  disabled={!!errors.firstName ||
                    !!errors.lastName ||
                    !!errors.email ||
                    !!errors.phone ||

                    updateProfileStatus === 'loading' ||
                    userProfile?.is_active === false ||
                    !hasChanges()}
                  aria-label="save"
                >

                  Save User Information Changes
                </Button>
              )}

            </div>

          </div>
        </div>
        {(isFinancialCoach || isSuperAdmin) && (
          <div className="flex flex-col justify-between items-center self-stretch border-l border-[#EAECF0] py-5 p-6 basis-2/5">
            <div className="flex flex-col items-start gap-5 self-stretch">
              <div className="flex items-start gap-4 self-stretch">
                <div className="flex flex-col justify-center items-start gap-1 flex-[1_0_0] self-stretch">
                  <div className="desktext11">Coaching Notes</div>
                </div>
                <div>
                  <Button outline
                    onClick={() => setIsAddNoteModalOpen(true)}
                    className="flex justify-center items-center px-[14px] py-[10px] gap-1 h-8 rounded-md border"
                  >
                    <img src={plusIcon} alt="plus" className="w-5 h-5" />Add New Note
                  </Button>
                </div>
              </div>
              <Divider />

              {/* Render coach notes list */}
              <div className="flex flex-col gap-3 w-full">
                {coachNotes.length === 0 && (
                  <div className="text-gray-400 text-sm">No coaching notes found.</div>
                )}
                {coachNotes.map((note) => {
                  let parsedOverview = "";
                  try {
                    const parsed = JSON.parse(note.sessionOverview || "{}");
                    parsedOverview = parsed?.root?.children
                      ?.map((child: any) =>
                        child?.children?.map((c: any) => c?.text || "").join(" ")
                      )
                      .join(" ")
                      .trim();
                  } catch {
                    parsedOverview = note.sessionOverview || "";
                  }

                  const formattedNote = {
                    coachName: `${note.coach?.first || "Coach"} ${note.coach?.last || ""}`.trim(),
                    createdAt: note.createdAt,
                    sessionOverview: parsedOverview,
                    topicsDiscussed: note.financialTopics?.map((t: any) => t.name) || [],
                    externalLinks: note.externalLinks || [],
                    attachments:
                      note.attachments?.map((a: any) => ({
                        name: a.fileName,
                        size: a.size || 0,
                        url: a.url || "",
                      })) || [],
                  };

                  return (
                    <div
                      key={note.id}
                      className="mb-6 cursor-pointer transition hover:opacity-80"
                      onClick={() => {
                        setSelectedNote(formattedNote);
                        setIsViewNoteModalOpen(true);
                      }}
                    >
                      {/* Date */}
                      <div className="text-center mb-2 text-gray-600 text5">
                        {new Date(note.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>

                      {/* Coaching Note Card */}
                      <ViewNoteSection note={formattedNote} />
                    </div>
                  );
                })}
              </div>
              {/* Pagination controls - page numbers with ellipsis */}
              {coachNotesTotalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-3 text-sm text-zinc-600 w-full">
                  <div>
                    Page {coachNotesPage} of {coachNotesTotalPages}
                  </div>

                  <div className="flex gap-2 items-center">
                    {/* Previous Button */}
                    <Button
                      outline
                      disabled={coachNotesPage === 1}
                      onClick={() => setCoachNotesPage(coachNotesPage - 1)}
                    >
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    {(() => {
                      const pages = [];
                      const total = coachNotesTotalPages;
                      const current = coachNotesPage;

                      const addPage = (num: number) => {
                        pages.push(
                          <Button
                            key={num}
                            {...(current === num ? { color: "blue" } : { outline: true })}
                            onClick={() => setCoachNotesPage(num)}
                          >
                            {num}
                          </Button>
                        );
                      };

                      // Always show first page
                      addPage(1);

                      // Show left ellipsis
                      if (current > 3) {
                        pages.push(<span key="left-ellipsis">...</span>);
                      }

                      // Show middle pages (current -1, current, current +1)
                      for (let p = current - 1; p <= current + 1; p++) {
                        if (p > 1 && p < total) addPage(p);
                      }

                      // Show right ellipsis
                      if (current < total - 2) {
                        pages.push(<span key="right-ellipsis">...</span>);
                      }

                      // Always show last page if more than 1 page
                      if (total > 1) addPage(total);

                      return pages;
                    })()}

                    {/* Next Button */}
                    <Button
                      outline
                      disabled={coachNotesPage === coachNotesTotalPages}
                      onClick={() => setCoachNotesPage(coachNotesPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}


              {/* Show expanded note modal only if selectedNote exists and coach is assigned */}
              {isViewNoteModalOpen && selectedNote && (
                <ViewNoteModal
                  isOpen={isViewNoteModalOpen}
                  onClose={() => setIsViewNoteModalOpen(false)}
                  note={selectedNote}
                />
              )}

            </div>
          </div>
        )}
      </div>

      {/* Add Note Modal */}
      {(isFinancialCoach || isSuperAdmin) && (
        <AddNoteModal
          userId={userId || ''}
          isOpen={isAddNoteModalOpen}
          onClose={handleCancelNote}
          onSave={handleSaveNote}
          initialData={noteData}
          onDataChange={setNoteData}
        />
      )}

    </div>
  );
};

export default UserProfile;
